import React from 'react';
import { AlertCircle, RefreshCw, Loader2, Database, FileX } from 'lucide-react';
import { AsyncState } from '../../hooks/useAsyncState';

interface LoadingStateProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  fullScreen?: boolean;
}

/**
 * Unified loading spinner component
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  size = 'medium',
  fullScreen = false
}) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  const Component = (
    <div className="flex flex-col items-center justify-center space-y-4 p-8">
      <Loader2 className={`${sizeClasses[size]} animate-spin text-primary`} />
      {message && (
        <p className="text-sm text-muted-foreground animate-pulse">
          {message}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
        {Component}
      </div>
    );
  }

  return Component;
};

interface ErrorStateProps {
  error: Error | string | null;
  onRetry?: () => void;
  fullScreen?: boolean;
  showDetails?: boolean;
}

/**
 * Unified error display component
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  error,
  onRetry,
  fullScreen = false,
  showDetails = false
}) => {
  const errorMessage = error instanceof Error ? error.message : error || 'An error occurred';
  const errorDetails = error instanceof Error ? error.stack : null;

  const Component = (
    <div className="flex flex-col items-center justify-center space-y-4 p-8">
      <div className="rounded-full bg-destructive/10 p-3">
        <AlertCircle className="w-8 h-8 text-destructive" />
      </div>
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold">Something went wrong</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {errorMessage}
        </p>
      </div>
      {showDetails && errorDetails && (
        <details className="text-xs text-muted-foreground max-w-md">
          <summary className="cursor-pointer hover:text-foreground">
            Show details
          </summary>
          <pre className="mt-2 p-2 bg-muted rounded overflow-auto">
            {errorDetails}
          </pre>
        </details>
      )}
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Try Again</span>
        </button>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
        {Component}
      </div>
    );
  }

  return Component;
};

interface EmptyStateProps {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Unified empty state component
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title = 'No data found',
  description = 'There is nothing to display at the moment.',
  icon = <Database className="w-12 h-12 text-muted-foreground" />,
  action
}) => {
  return (
    <div className="flex flex-col items-center justify-center space-y-4 p-12">
      <div className="rounded-full bg-muted p-4">
        {icon}
      </div>
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {description}
        </p>
      </div>
      {action && (
        <button
          onClick={action.onClick}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

interface AsyncStateDisplayProps<T> {
  state: AsyncState<T>;
  onRetry?: () => void;
  loadingMessage?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: {
    label: string;
    onClick: () => void;
  };
  children: (data: T) => React.ReactNode;
}

/**
 * Wrapper component that handles all async states
 */
export function AsyncStateDisplay<T>({
  state,
  onRetry,
  loadingMessage,
  emptyTitle,
  emptyDescription,
  emptyAction,
  children
}: AsyncStateDisplayProps<T>) {
  // Loading state
  if (state.loading) {
    return <LoadingState message={loadingMessage} />;
  }

  // Error state
  if (state.error) {
    return <ErrorState error={state.error} onRetry={onRetry || state.retry} />;
  }

  // Empty state
  if (state.isEmpty) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  // Success state with data
  if (state.data) {
    return <>{children(state.data)}</>;
  }

  // Fallback (shouldn't happen)
  return <EmptyState />;
}

interface SkeletonProps {
  className?: string;
  animate?: boolean;
}

/**
 * Skeleton loading placeholder
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  animate = true
}) => {
  return (
    <div
      className={`
        bg-muted rounded
        ${animate ? 'animate-pulse' : ''}
        ${className}
      `}
    />
  );
};

interface CardSkeletonProps {
  count?: number;
}

/**
 * Card skeleton for loading states
 */
export const CardSkeleton: React.FC<CardSkeletonProps> = ({ count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="border rounded-lg p-6 space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
          </div>
          <div className="flex justify-between">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
        </div>
      ))}
    </>
  );
};

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

/**
 * Table skeleton for loading states
 */
export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = 4
}) => {
  return (
    <div className="w-full">
      {/* Header */}
      <div className="border-b p-4">
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} className="h-4" />
          ))}
        </div>
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="border-b p-4">
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={colIndex} className="h-3" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

interface ProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  showPercentage?: boolean;
  color?: 'primary' | 'success' | 'warning' | 'danger';
}

/**
 * Progress bar component
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  label,
  showPercentage = false,
  color = 'primary'
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  const colorClasses = {
    primary: 'bg-accent-500',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-error'
  };

  return (
    <div className="space-y-2">
      {(label || showPercentage) && (
        <div className="flex justify-between text-sm">
          {label && <span className="text-muted-foreground">{label}</span>}
          {showPercentage && <span className="font-medium">{percentage.toFixed(0)}%</span>}
        </div>
      )}
      <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} transition-all duration-300 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

interface RetryBoundaryProps {
  children: React.ReactNode;
  fallback?: (error: Error, retry: () => void) => React.ReactNode;
}

interface RetryBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

/**
 * Error boundary with retry capability
 */
export class RetryBoundary extends React.Component<RetryBoundaryProps, RetryBoundaryState> {
  constructor(props: RetryBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<RetryBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('RetryBoundary caught error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      return (
        <ErrorState
          error={this.state.error}
          onRetry={this.handleRetry}
          showDetails={true}
        />
      );
    }

    return this.props.children;
  }
}
